import cn from 'classnames';
import getIsStudioSelectionProduct from 'store/product/selector/getIsStudioSelectionProduct';
import removeGetParams from 'utils/routes/removeGetParams';
import Backdrop from 'components/Common/Backdrop/Backdrop';
import Spacer from 'components/Common/Spacer/Spacer';
import StudioSelectionSearchBar from 'components/Common/StudioSelectionSearchBar/StudioSelectionSearchBar';
import Layout from 'components/Layout/Layout';
import AddToBasketButtonPremium from 'components/ProductPage/AddToBasketButtonPremium/AddToBasketButtonPremium';
import AddToBasketCounterTracker from 'components/ProductPage/AddToBasketCounterTracker/AddToBasketCounterTracker';
import BundleProducts from 'components/ProductPage/BundleProducts/BundleProducts';
import CuratedWallpaperPostAddToCartFlow from 'components/ProductPage/CuratedWallpaperPostAddToCartFlow/CuratedWallpaperPostAddToCartFlow';
import OpStickyBar from 'components/Common/OpStickyBar/OpStickyBar';
import DesignDetails from 'components/ProductPage/DesignDetails/DesignDetails';
import OPPlusTransitionReminder from 'components/ProductPage/OPPlusTransitionReminder/OPPlusTransitionReminder';
import PostAddToCartFlow from 'components/ProductPage/PostAddToCartFlow/PostAddToCartFlow';
import ProductAttributeDescription from 'components/ProductPage/ProductAttributeDescription/ProductAttributeDescription';
import ProductAttributes from 'components/ProductPage/ProductAttributes/ProductAttributes';
import ProductBreadcrumbs from 'components/ProductPage/ProductBreadcrumbs/ProductBreadcrumbs';
import ProductDetails from 'components/ProductPage/ProductDetails/ProductDetails';
import ProductPreviewDesktop from 'components/ProductPage/ProductPreviewDesktop/ProductPreviewDesktop';
import ProductPreviewMobile from 'components/ProductPage/ProductPreviewMobile/ProductPreviewMobile';
import ProductPrice from 'components/ProductPage/ProductPrice/ProductPrice';
import ProductTitle from 'components/ProductPage/ProductTitle/ProductTitle';
import SupportMaterials from 'components/ProductPage/SupportMaterials/SupportMaterials';
import UspsPremium from 'components/ProductPage/UspsPremium/UspsPremium';
import {
  BLUESHIFT_OTHERS_ALSO_BOUGHT_SLOT,
  BLUESHIFT_POPULAR_IN_THE_SAME_CATEGORY_PRODUCTS_SLOT,
  WALLPAPER_PRODUCT_TYPES,
} from 'constants/app';
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { matchPath, useLocation, useParams } from 'react-router-dom';
import { Store } from 'store';
import { getIsGettySearchEnabled, getIsStudioSelectionCategory } from 'store/app/selector/getConfigurationByKey';
import isDesktop from 'store/app/selector/isDesktop';
import fetchCurrentCategoryOperation from 'store/category/operation/fetchCurrentCategoryOperation';
import getFetchedCategoryId from 'store/category/selector/getFetchedCategoryId';
import { setCategoryIdAction } from 'store/category/slice';
import fetchPricesOperation from 'store/price/operation/fetchPricesOperation';
import fetchDeliveryDetailsOperation from 'store/product/operation/fetchDeliveryDetailsOperation';
import fetchDeliveryOptionsOperation from 'store/product/operation/fetchDeliveryOptionsOperation';
import fetchMatchingProducts from 'store/product/operation/fetchMathingProducts';
import AttributeSelectionMobilePopup from 'components/ProductPage/AttributeSelectionMobilePopup/AttributeSelectionMobilePopup';
import fetchMoreArtPrintsOperation from 'store/product/operation/fetchMoreArtPrintsOperation';
import fetchOPPlusContentOperation from 'store/product/operation/fetchOPPlusContentOperation';
import fetchProductOperation from 'store/product/operation/fetchProductOperation';
import fetchProductRecommendationOperation from 'store/product/operation/fetchProductRecommendationsOperation';
import resetProductPageDataOperation from 'store/product/operation/resetProductPageDataOperation';
import setParamsFromQueryStringServerOperation from 'store/product/operation/setParamsFromQueryStringServerOperation';
import getProductItem from 'store/product/selector/getProductItem';
import {
  setAddingToCartFlowAction,
  setEncPublicDesignIdAction,
  setInitialProductFlagAction,
} from 'store/product/slice';
import { AddToCartFlow, ProductRecommendationSlot } from 'store/product/types';
import SSRParams from 'utils/class/SSRParams';
import gtmSendFunnelStepEvent, { FUNNEL_STEPS } from 'utils/gtm/gtmSendFunnelStepEvent';
import scrollPageTop from 'utils/viewport/scrollPageTop';
import { Recommendations } from 'components/Common';
import getProductRecommendationByKey from 'store/product/selector/getProductRecommendationByKey';
import fetchOthersAlsoBoughtOperation from 'store/product/operation/fetchOthersAlsoBoughtProductsOperation';
import fetchGTMProductDataOperation from 'store/category/operation/fetchGTMProductDataOperation';
import OthersAlsoBought from 'components/ProductPage/OthersAlsoBought/OthersAlsoBought';
import ProductTitleMobile from 'components/ProductPage/ProductTitleMobile/ProductTitleMobile';
import sendGtmProductViewOperation from 'store/category/operation/sendGtmProductViewOperation';
import gtmSendEvent from 'utils/gtm/gtmSendEvent';
import gtmBlueshiftRecommendationEvent from 'utils/gtm/gtmBlueshiftRecommendationEvent';
import { Recommendation } from 'store/landing/slice';
import getProductId from 'store/product/selector/getProductId';
import getCurrentCategory from 'store/category/selector/getCurrentCategory';
import getIsGettyDesignProduct from 'store/product/selector/getIsGettyDesignProduct';
import storeRecentlyViewedOperation from 'store/product/operation/storeRecentlyViewedOperation';
import RecentlyViewedBlock from 'components/Common/RecentlyViewed/RecentlyViewedBlock';
import checkIfSticky from 'utils/checkIfSticky';
import SizeGuidePopup from 'components/ProductPage/SizeGuidePopup/SizeGuidePopup';
import OpFavourite from 'components/Common/op-ui-wrappers/OpFavourite/OpFavourite';
import DeliveryAndOPPlusFreeShipping from 'components/ProductPage/DeliveryAndOPPlusFreeShipping/DeliveryAndOPPlusFreeShipping';
import gtmRecommendationClickEvent from 'utils/gtm/gtmRecommendationClickEvent';
import getCategoryId from 'store/category/selector/getCategoryId';
import useScrollIntoView from 'hooks/useSrollIntoView';
import gtmRecommendationViewEvent from 'utils/gtm/gtmRecommendationViewEvent';
import LastOrderDate from 'components/Common/LastOrderDate/LastOrderDate';
import HelloBar from '../../components/Common/HelloBar/HelloBar';
import styles from './ProductPage.module.scss';

interface ProductPageURLParams {
  cid: string;
  design_id: string;
}

const ProductPage = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { cid: categoryId, design_id: designId } = useParams<ProductPageURLParams>();
  const isDesktopDevice = useSelector(isDesktop);
  const fetchedCategoryId = useSelector(getFetchedCategoryId);
  const isGettySearchEnabled = useSelector(getIsGettySearchEnabled);
  const isStudioSelection = useSelector((store: Store) => getIsStudioSelectionCategory(store, Number(categoryId)));
  const productItem = useSelector(getProductItem);
  const breadCrumbs = useSelector(getCurrentCategory)?.breadcrumbs;
  const isGettyDesignProduct = useSelector(getIsGettyDesignProduct);
  const isStudioDesignProduct = useSelector(getIsStudioSelectionProduct);
  const productId = useSelector(getProductId);
  const isCuratedWallPaperDesign = productItem?.product.productTypeId === 176;
  const isWallpaper = WALLPAPER_PRODUCT_TYPES.includes(productItem?.product.productTypeId || -1);
  const priceRemark = isWallpaper ? '*' : '';
  const addToBasketButtonText = isCuratedWallPaperDesign ? t('txt_fit_to_wall') : undefined;
  const categoryName = (useSelector(getCurrentCategory) as any)?.name;
  const currentCategoryId = useSelector(getCategoryId);
  const popularInTheSameCategory = useSelector(
    getProductRecommendationByKey(ProductRecommendationSlot.POPULAR_IN_THE_SAME_CATEGORY),
  );
  const theme = isStudioSelection ? 'studio' : 'default';
  const stickyButtonRef = useRef<HTMLDivElement>(null);
  const trackBlueshiftRecommendation = (product: Recommendation) => {
    if (!product || !currentCategoryId) return;
    gtmSendEvent(gtmBlueshiftRecommendationEvent(product, 'product', productId, productItem?.product.productUid || ''));
    gtmSendEvent(gtmRecommendationClickEvent('Suggested For You', categoryName, currentCategoryId, product.categoryId));
  };
  const helloBar = useSelector(getCurrentCategory)?.helloBar;
  const { ref: recommendationsRef, isVisible: recommendationsVisible } = useScrollIntoView();
  const { ref: othersAlsoBoughtRef, isVisible: othersAlsoBoughtVisible } = useScrollIntoView();

  useEffect(() => {
    gtmSendEvent(gtmRecommendationViewEvent('Suggested For You', categoryName));
  }, [recommendationsVisible]);

  useEffect(() => {
    gtmSendEvent(gtmRecommendationViewEvent('Others Also Bought', categoryName));
  }, [othersAlsoBoughtVisible]);

  const needToShowAttributeSelectionPopup = () => {
    if (isDesktopDevice) return false;
    if (!stickyButtonRef || !stickyButtonRef.current) return false;
    stickyButtonRef.current.style.position = 'sticky';
    return checkIfSticky(stickyButtonRef.current);
  };

  useEffect(() => {
    if (!breadCrumbs) return;
    let extraTag = '/private/';
    const parentUrl = breadCrumbs.slice(-1)[0].url;
    extraTag += breadCrumbs ? `${removeGetParams(parentUrl)}` : '';
    extraTag = extraTag.replaceAll('//', '/');
    extraTag = isGettyDesignProduct ? `${extraTag}/getty` : extraTag;
    extraTag = isStudioDesignProduct ? `${extraTag}/studioselection` : extraTag;
    gtmSendFunnelStepEvent(FUNNEL_STEPS.productPage, extraTag);
  }, [location.pathname, breadCrumbs]);

  useEffect(() => {
    dispatch(storeRecentlyViewedOperation(designId));
  }, [productItem?.product.productTypeId]);

  useEffect(() => {
    scrollPageTop();
    dispatch(resetProductPageDataOperation());
    dispatch(setCategoryIdAction(Number(categoryId)));
    dispatch(fetchOthersAlsoBoughtOperation(Number(categoryId)));
    dispatch(setEncPublicDesignIdAction(designId));
    dispatch(setInitialProductFlagAction(true));
    if (fetchedCategoryId !== Number(categoryId)) {
      dispatch(fetchCurrentCategoryOperation());
    }
    // TODO: Remove unknown from next line

    (dispatch(fetchProductOperation()) as unknown as Promise<void>).then(() => {
      dispatch(fetchPricesOperation());
      Promise.all([
        dispatch(fetchOPPlusContentOperation()),
        dispatch(fetchMoreArtPrintsOperation()),
        dispatch(fetchGTMProductDataOperation()),
        dispatch(
          fetchProductRecommendationOperation(
            BLUESHIFT_OTHERS_ALSO_BOUGHT_SLOT,
            ProductRecommendationSlot.OTHERS_ALSO_BOUGHT,
            false,
          ),
        ) as unknown as Promise<void>,
        dispatch(
          fetchProductRecommendationOperation(
            BLUESHIFT_POPULAR_IN_THE_SAME_CATEGORY_PRODUCTS_SLOT,
            ProductRecommendationSlot.POPULAR_IN_THE_SAME_CATEGORY,
            true,
          ),
        ) as unknown as Promise<void>,
      ]).then(() => {
        dispatch(sendGtmProductViewOperation());
        dispatch(fetchPricesOperation());
      });
      dispatch(fetchMatchingProducts());
      dispatch(fetchDeliveryDetailsOperation());
      dispatch(fetchDeliveryOptionsOperation());
    });
    return () => {
      dispatch(setAddingToCartFlowAction(AddToCartFlow.idle));
    };
  }, [location.pathname]);

  return (
    <>
      <Layout
        itemType="http://schema.org/Product"
        itemScope
        className={styles.predesignedTheme}
        showScrollTop={isDesktopDevice}
        prefix={<OpStickyBar />}
      >
        <meta itemProp="brand" content="Optimalprint" />
        {isDesktopDevice && <Spacer x={1} />}
        <HelloBar cleanupContentStyles={!isDesktopDevice} content={helloBar} />
        {isStudioSelection && isGettySearchEnabled && (
          <StudioSelectionSearchBar categoryId={fetchedCategoryId} className={styles.searchBar} />
        )}
        <div className={cn(styles.row, styles.breadcrumbs)}>
          <ProductBreadcrumbs />
        </div>
        <div className={cn(styles.productPage, { [styles.mobile]: !isDesktopDevice })}>
          {isDesktopDevice && (
            <div className={styles.column}>
              <ProductPreviewDesktop />
              {productItem?.product.sku && (
                <div className={styles.favouriteIcon}>
                  <OpFavourite
                    sku={productItem.product.sku}
                    categoryId={productItem.product.categoryId}
                    productTier={productItem.product.productTier}
                  />
                </div>
              )}
            </div>
          )}
          {!isDesktopDevice && <ProductPreviewMobile />}
          <div className={cn(styles.column, styles.dataColumn)}>
            <div>
              {isDesktopDevice && <ProductTitle />}
              {!isDesktopDevice && <ProductTitleMobile />}
            </div>
            <ProductPrice remark={priceRemark} inOneLine={!isDesktopDevice} />
            {/* <OpShippingInfo productId={productId} quantity={quantity} onReload={setShowDeliveryOptions} /> */}
            {/* <div className={styles.deliveryDetails}>
            <ProductionCountry />
            <EstimatedShipmentPrice />
            <OPPlusFreeShipping />
          </div> */}
            <ProductAttributes />
            <BundleProducts />
            <div
              ref={stickyButtonRef}
              className={cn(styles.buttonContainer, { [styles.stickyButton]: !isDesktopDevice })}
            >
              <AddToBasketButtonPremium
                primary
                buttonTitle={addToBasketButtonText}
                openMobilePopupCheck={needToShowAttributeSelectionPopup}
              />
            </div>
            <LastOrderDate type="sm" theme="studio" productId={productId} />
            <DeliveryAndOPPlusFreeShipping />
            {priceRemark && (
              <div className={styles.priceRemark}>
                <sup>*</sup>
                {t('txt_price_per_square_meter')}
              </div>
            )}
            <OPPlusTransitionReminder />
            <ProductDetails />
            <UspsPremium />
            {isWallpaper && (
              <ProductAttributeDescription
                selectorTypeName="UnifiedPaperType"
                sectionTitle={t('txt_paper_type_description')}
              />
            )}
            <DesignDetails />
            <SupportMaterials />
          </div>
        </div>
        <div className={styles.space} />
        {/* <MoreArtPrints /> */}
        {/* <div className={styles.row}>
        <Recommendations source={othersAlsoBought} sectionHeader={t('txt_others_also_bought')} />
      </div> */}
        <div className={styles.block} ref={othersAlsoBoughtRef}>
          <OthersAlsoBought theme={theme} />
        </div>

        {popularInTheSameCategory && !isWallpaper && (
          <div className={cn(styles.block)} ref={recommendationsRef}>
            <Recommendations
              source={popularInTheSameCategory}
              sectionHeader={t('txt_suggested_for_you')}
              gaTrackingHandler={trackBlueshiftRecommendation}
            />
          </div>
        )}

        <div className={cn(styles.block)}>
          <RecentlyViewedBlock theme={theme} />
        </div>
        {/*
      <div className={styles.row}>
        <MatchingProducts products={matchingProducts} sectionHeader={t('txt_also_available_on')} />
      </div> */}
        {/* <div className={styles.row}>
        <RecentlyViewed />
      </div> */}
        {/* <div className={styles.row}>
        <TrustpilotFeedback />
      </div> */}
        {/* {!isDesktopDevice && (
        <div className={styles.row}>
          <SeeAllDesigns />
        </div>
      )} */}
        {/* <BuyCollection /> */}
        <Backdrop />
        <SizeGuidePopup />
        {!isDesktopDevice && <AttributeSelectionMobilePopup />}
        {!isCuratedWallPaperDesign && <PostAddToCartFlow />}
        {isCuratedWallPaperDesign && <CuratedWallpaperPostAddToCartFlow />}
        <AddToBasketCounterTracker />
      </Layout>
    </>
  );
};

export default ProductPage;

const fetchProductData = (params: SSRParams) => [
  params.dispatch(fetchCurrentCategoryOperation()),
  params.dispatch(fetchProductOperation()),
];

// Executed on the server to prepare initial data
export const fetchProductPageInitialData = (params: SSRParams): Promise<any>[] => {
  // TODO: get params from URL, store them
  const router = matchPath(params.req.path, {
    path: params.path,
    exact: true,
    strict: true,
  });
  params.dispatch(setParamsFromQueryStringServerOperation(router?.params, params.req.query));
  // Should return array of promises to resolve on the server
  return fetchProductData(params);
};
